import {
  ArdoqId,
  APIComponentAttributes,
  ComponentBatchDeleteResponse,
  Entity,
  ProvenanceSource,
} from '@ardoq/api-types';
import { actionCreator } from '@ardoq/rxbeach';
import { ArdoqError, CidArgs } from '@ardoq/common-helpers';
import { LocallyCreatedComponentAttributes } from '@ardoq/component-interface';

export type AddComponentsPayload = {
  components: APIComponentAttributes[];
};

export type RemoveComponentsPayload = {
  ids: ArdoqId[];
};

export const addComponentsAction = actionCreator<AddComponentsPayload>(
  '[ComponentActions] ADD_COMPONENTS'
);

export const resetComponentsAction = actionCreator(
  '[ComponentActions] RESET_COMPONENTS'
);

export type ResetComponentsWithDataPayload = {
  components: APIComponentAttributes[];
};

export const resetComponentsWithData =
  actionCreator<ResetComponentsWithDataPayload>(
    '[ComponentActions] RESET_COMPONENTS_WITH_DATA'
  );

export const removeComponentsAction = actionCreator<RemoveComponentsPayload>(
  '[ComponentActions] REMOVE_COMPONENTS'
);

export const updateComponentAction = actionCreator<APIComponentAttributes>(
  '[ComponentActions] UPDATE_COMPONENT'
);

export const mergeComponentChange = actionCreator<
  Partial<APIComponentAttributes> & Entity
>('[ComponentActions] MERGE_COMPONENT_CHANGE');

export const bulkComponentsDeleted =
  actionCreator<ComponentBatchDeleteResponse>(
    '[ComponentActions] BULK_COMPONENTS_DELETED'
  );

export const setScenarioRelatedComponentIds = actionCreator<ArdoqId[]>(
  '[ComponentActions] SET_SCENARIO_RELATED_COMPONENT_IDS'
);

export type CreateComponentPayload = {
  attributes: {
    rootWorkspace: ArdoqId;
    parent: ArdoqId | null;
    name: string;
    typeId: string;
  };
  provenanceSource?: ProvenanceSource;
};

export const selectComponent = actionCreator<CidArgs>(
  '[ComponentActions] SELECT_COMPONENT'
);

export const componentSelectedInPresentationMode = actionCreator<{
  id: ArdoqId;
}>('[ComponentActions] COMPONENT_SELECTED_IN_PRESENTATION_MODE');

export const toggleComponentDescription = actionCreator<CidArgs>(
  '[ComponentActions] TOGGLE_COMPONENT_DESCRIPTION'
);

export const deleteComponent = actionCreator<ArdoqId>(
  '[ComponentActions] COMPONENT_DELETE'
);

export const toggleLockComponent = actionCreator<ArdoqId>(
  '[ComponentActions] COMPONENT_TOGGLE_LOCK_COMPONENT'
);

export type SetComponentLockPayload = {
  componentId: ArdoqId;
  locked: boolean;
};

export const setComponentLock = actionCreator<SetComponentLockPayload>(
  '[ComponentActions] SET_COMPONENT_LOCK'
);

export const notifyComponentLocked = actionCreator<ArdoqId>(
  '[ComponentActions] NOTIFY_COMPONENT_LOCKED'
);

export const notifyComponentUnlocked = actionCreator<ArdoqId>(
  '[ComponentActions] NOTIFY_COMPONENT_UNLOCKED'
);

export type LockComponentsPayload = {
  componentIds: ArdoqId[];
  userId: ArdoqId;
};

export const lockComponents = actionCreator<LockComponentsPayload>(
  '[ComponentActions] LOCK_COMPONENTS'
);

export const unlockComponents = actionCreator<ArdoqId[]>(
  '[ComponentActions] UNLOCK_COMPONENTS'
);

export const createSurveyFromComponent = actionCreator<ArdoqId>(
  '[ComponentActions] COMPONENT_CREATE_SURVEY_FROM_OMPONENT'
);

export const createComponent = actionCreator<CreateComponentPayload>(
  '[ComponentActions] COMPONENT_CREATE'
);

export const createComponentFailed = actionCreator(
  '[ComponentActions] COMPONENT_CREATE_FAILED'
);

export const createComponentSucceeded = actionCreator<APIComponentAttributes>(
  '[ComponentActions] COMPONENT_CREATE_SUCCESS'
);

export type CreateComponentsPayload = {
  components: LocallyCreatedComponentAttributes[];
  provenanceSource: ProvenanceSource;
};

export const createComponents = actionCreator<CreateComponentsPayload>(
  '[ComponentActions] COMPONENTS_CREATE'
);

export const createComponentsSucceeded = actionCreator<
  APIComponentAttributes[]
>('[ComponentActions] COMPONENTS_CREATE_SUCCESS');

export const createComponentsFailed = actionCreator<ArdoqError>(
  '[ComponentActions] COMPONENTS_CREATE_FAILED'
);

export const batchLoadComponents = actionCreator<APIComponentAttributes[]>(
  '[ComponentActions] BATCH_LOAD_COMPONENTS'
);

export type MergeComponentsPayload = {
  components: APIComponentAttributes[];
};

export const mergeComponentsAction = actionCreator<MergeComponentsPayload>(
  '[ComponentActions] MERGE_COMPONENTS'
);

export const imageDroppedOnComponentNode = actionCreator<{
  componentId: ArdoqId;
  file: File;
}>('[ComponentActions] IMAGE_DROPPED_ON_COMPONENT_NODE');

export type SaveComponentsPayload = {
  attributes: APIComponentAttributes[];
  isScenarioMode: boolean;
  provenanceSource: ProvenanceSource;
};

export const saveComponentsAction = actionCreator<SaveComponentsPayload>(
  '[ComponentActions] SAVE_COMPONENTS'
);
